import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { FaChevronDown, FaChevronRight, FaExternalLinkAlt } from 'react-icons/fa';
import './Research.css';

const Research = () => {
  const [ref] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const [expandedSections, setExpandedSections] = useState({
    publications: true,
    workingPapers: true,
    csPreprints: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const publications = [
    {
      title: "Management Disclosure and Media Coverage",
      authors: "joint with Hamid Boustanifar",
      journal: "The Journal of Financial and Quantitative Analysis (JFQA), 2026",
      links: [
        { text: "link to journal", url: "https://doi.org/10.1017/S0022109026102634" },
        { text: "link to SSRN", url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5421137" },
        { text: "link to dataset", url: "https://www.dropbox.com/scl/fi/tjb5ix6j7hkfe73vgs3qz/DatasetBoustanifar-MansouriJFQA.zip?rlkey=hfilwvutlpq0aaz9562h8to0m&dl=0" },
        { text: "extended data, 2001–2026", url: "/econlinguistics/#data" }
      ]
    },
    {
      title: "Conflicted Analysts and Initial Coin Offerings",
      authors: "joint with Andreas Barth, Valerie Laturnus, and Alexander F. Wagner",
      journal: "Management Science, 2023",
      links: [
        { text: "link to the journal", url: "https://doi.org/10.1287/mnsc.2021.02928" }
      ]
    },
    {
      title: "\"Let me get back to you\" - A machine learning approach to measuring non-answers",
      authors: "joint with Andreas Barth, and Fabian Woebbeking",
      journal: "Management Science, 2023",
      links: [
        { text: "link to the journal", url: "https://pubsonline.informs.org/doi/full/10.1287/mnsc.2022.4597" },
        { text: "web application", url: "/econlinguistics/" },
        { text: "glossary of non-answer phrases", url: "/econlinguistics/#glossary" }
      ]
    },
    {
      title: "Financing Sustainable Entrepreneurship: ESG Measurement, Valuation, and Performance",
      authors: "joint with Paul P. Momtaz",
      journal: "Journal of business Venturing, 2022",
      links: [
        { text: "link to the journal", url: "https://www.sciencedirect.com/science/article/pii/S0883902622000702" },
        { text: "link to the project homepage", url: "http://sustainableentrepreneurship.org/" }
      ]
    },
    {
      title: "Why Do Video Pitches Matter in Crowdfunding?",
      authors: "joint with Maura Kolbe, and Paul P. Momtaz",
      journal: "Journal of Economics and Business, 2022",
      links: [
        { text: "link to the journal", url: "https://www.sciencedirect.com/science/article/pii/S0148619522000376?via%3Dihub" }
      ]
    },
    {
      title: "Corporate Culture and Banking",
      authors: "joint with Andreas Barth",
      journal: "Journal of Economic Behavior & Organization, June 2021",
      links: [
        { text: "link to the journal page", url: "https://doi.org/10.1016/j.jebo.2021.02.010" }
      ]
    }
  ];

  const workingPapers = [
    {
      title: "Same Text, Different Numbers: The Divergence of LLM-Based Measures",
      authors: "joint with Hamid Boustanifar",
      status: "",
      abstract: "Researchers increasingly use generative large language models (LLMs) to convert corporate text into empirical variables. We examine the extent to which LLM-based textual measures are invariant to model choice using thirteen measures, including sentiment, management clarity, uncertainty, answer specificity, and climate and political risk. Seven LLMs from different providers score earnings call transcripts of S&P 500 companies on these constructs. Cross-model rank correlations average only 0.52, and transcript-level differences common across providers account for only 34% of total score variation. Cross-model disagreement does not predict subsequent analyst or market disagreement, consistent with a substantial model-specific component rather than common ambiguity in the underlying disclosure. Model choice significantly affects downstream inference, with coefficient magnitudes, signs, and statistical significance varying substantially across models. Averaging across providers makes transcript rankings more stable for most constructs, but score levels remain sensitive to the models included in the ensemble. LLM-generated variables should therefore be treated as model-contingent measurements and validated across providers.",
      links: [
        { text: "link to SSRN", url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=7519558" },
        { text: "link to arXiv", url: "https://arxiv.org/abs/2609.31013" }
      ]
    },
    {
      title: "Labeled Irrelevant: Materiality Standards and the Pricing of ESG Misconduct",
      authors: "joint with Aleksandra Bakhareva, Mark Wahrenburg, and Fabian Woebbeking",
      status: "",
      abstract: "Do financial analysts punish environmental, social, and governance (ESG) misconduct itself, or only the financial risk it carries? We exploit the staggered release of the Sustainability Accounting Standards Board (SASB) materiality classifications between 2013 and 2016 as shocks to the information environment of S&P 500 firms. Before the standards, analysts penalize material and non-material incidents alike in their price targets. Afterwards, the penalty for non-material incidents disappears, while the reaction to material incidents remains unchanged. This adjustment brings no detectable gain in forecast accuracy and is concentrated among the least precise analysts, consistent with the public label replacing their own judgment. Earnings calls reveal the same substitution among managers. Once the classifications become public, managers stop pre-emptively explaining non-material incidents when analysts raise ESG topics, while responses to non-ESG topics remain unchanged. Materiality standards might therefore improve pricing efficiency by removing cash-flow irrelevant noise. However, this improvement comes at the cost of reducing scrutiny for severe yet \"non-material\" misconduct as analysts and managers withdraw their attention.",
      links: [
        { text: "link to SSRN", url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=7665859" }
      ]
    },
    {
      title: "AI-Generated Fundraising Campaigns: Enhancing Donations or Eroding Trust?",
      authors: "joint with Alexandra Niessen-Ruenzi and Amir Sepehri",
      status: "",
      abstract: "We examine how the use of AI in writing fundraising campaigns influences donation behavior. Analyzing over 117,000 real GoFundMe campaigns, we find that campaigns more similar to AI-generated text raise more money. We corroborate this result with a pre-registered online experiment, in which participants were shown campaigns written by either humans or AI. Again, we find that AI-written campaigns attract more donations than human-written ones. However, AI usage disclosure leads to opposite results: When donors knew AI was used, donations dropped even below the level of human-written campaigns. Disclosing the use of AI in campaign creation may induce perceptions of inauthenticity or manipulation, thereby reducing donor trust and willingness to contribute.",
      links: [
        { text: "link to SSRN", url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5399778" }
      ]
    },
    {
      title: "How to talk down your stock performance",
      authors: "joint with Andreas Barth, Fabian Woebbeking, and Severin Zoergiebel",
      status: "in revision",
      abstract: "Context specific language or jargon helps, by definition, to efficiently and precisely transfer information. However, due to its complex nature, jargon might also be a tool to obfuscate information. This paper studies whether jargon is used in verbal firm disclosures to obfuscate or to efficiently transfer information. We observe that, within the Q&A of earnings conference calls, managers use less jargon in responses to tougher questions, and in calls after a quarter of bad success. Moreover, markets interpret the lack of precise information as a bad signal: we find lower cumulative abnormal returns and a higher implied volatility following earnings calls where managers use less jargon. These results support the argument that an excessive use of non-factual language is perceived as blathering that retards the reduction of information asymmetries.",
      links: [
        { text: "link to SSRN", url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=3336671" }
      ]
    },
    {
      title: "Information Flow and Market Efficiency - Unintended Side Effects of the Plain Writing Act",
      authors: "joint with Andreas Barth and Fabian Woebbeking",
      status: "",
      abstract: "This paper investigates the impact of precise language on market efficiency by examining the interplay between regulatory language simplification, as imposed by the Plain Writing Act of 2010, and information asymmetries in financial markets. We posit that financial jargon, while often criticized as complex, plays a pivotal role in facilitating efficient information dissemination within the market. Analyzing earnings conference call text data, we employ an instrumental variable event study framework to evaluate the consequences of the Plain Writing Act. Our findings suggest that the Act's promotion of plain language usage results in a reduction of financial jargon. Paradoxically, this reduction in jargon is associated with a decrease in market efficiency, implying that the Act may inadvertently hinder information flow and impair the market's price setting mechanism.",
      links: [
        { text: "link to SSRN", url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=4629494" }
      ]
    },
    {
      title: "Market Discipline in Banking: the Role of Financial Analysts",
      authors: "joint with Andreas Barth and Fabian Woebbeking",
      status: "",
      abstract: "Using a novel measure for analysts' attention during earnings conference calls, this paper studies how markets discipline banks. We consider two groups of banks: a treated group with implicit bail-out guarantees and an untreated group without such guarantees. Our analysis focuses on the information that analysts request from banks, which we classify using DebtBERT, a specifically trained large language model. We find that analysts increased their scrutiny post-global financial crisis. This increased attention affects banks' abnormal stock returns in the short-term and leverage in the long-term, which is especially notable in banks lacking bail-out guarantees. This result suggests a moral hazard problem, where investors strategically discipline banks based on their perception of bail-out guarantees. Our findings have important implications for regulatory and policy decisions aimed at promoting transparency and stability in the banking sector.",
      links: [
        { text: "link to SSRN", url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=4613690" }
      ]
    },
    // {
    //   title: "Political economy of climate risk and policy stringency",
    //   authors: "joint with Andreas Barth, Birte Winter, and Fabian Woebbeking",
    //   status: "",
    //   abstract: "This paper presents evidence for a regulatory stringency channel through which firm emissions affect firm value. Using the Paris Agreement as an exogenous shock to expected future environmental regulation, we show that high-polluting firms located in states with a high probability of adopting stricter environmental regulations are associated with lower realized returns. We show that this effect is strongest in states with stricter climate policy enforcement. These results suggest that high-polluting firms are at risk of facing additional regulatory stringency after large natural disasters, as citizens affected by frequent natural disasters may push for stricter environmental regulations in their state. Understanding the channels that translate policy change into investors (i.e. firm owners) behaviour is particularly relevant for the development of effective (climate) policy.",
    //   links: [
    //     { text: "Available upon request", url: "" }
    //   ]
    // }
  ];

  // Computer science papers, released as arXiv preprints rather than following the working paper -> journal path
  const csPreprints = [
    {
      title: "FinRank: An Evidence-Grounded Benchmark for Financial Question Answering and Retrieval over SEC Filings",
      authors: "joint with Daniel Saad, Mark Wahrenburg, Manu Weissel, and Fabian Woebbeking",
      status: "",
      abstract: "Financial question answering is typically evaluated by answer correctness, yet in SEC filings a plausible and even numerically correct answer can be grounded in the wrong evidence. Similar facts and disclosures recur across sections of a filing, across reporting periods of the same firm, and across comparable firms. FinRank targets this provenance-sensitive retrieval problem by requiring systems to identify evidence for the intended entity, reporting period, and disclosure context. The benchmark contains 1185 manually authored question-answer records over the 10-K and 10-Q filings of 22 companies. Each record includes a reference answer, gold supporting passages, and hand-curated hard negatives drawn from confusable passages within filings, across reporting periods, and across comparable firms. FinRank evaluates passage retrieval, reranking, and hard-negative discrimination as separately measured tasks. Baseline results demonstrate the difficulty of this setting: among the evaluated systems, even a 7B instruction-tuned embedder reaches only 44.8% Recall@10 on the pooled evidence corpus; sub-billion-parameter encoders gain at most 3.5 points over BM25, a finance-adapted embedder trails BM25 by 9.7 points, and pairwise accuracy falls by 13.0-20.5 percentage points when random negatives are replaced with the curated hard negatives. FinRank provides an evidence-first benchmark for developing financial question answering systems that are not only accurate but also grounded in the correct disclosure.",
      links: [
        { text: "link to arXiv", url: "https://arxiv.org/abs/2608.07400" },
        { text: "dataset and code", url: "https://github.com/datanxt/FinRank" }
      ]
    },
    {
      title: "Bypassing Document Ingestion: An MCP Approach to Financial Q&A",
      authors: "joint with Edoardo Pilla, Mark Wahrenburg, and Fabian Woebbeking",
      status: "",
      abstract: "Answering financial questions is often treated as an information retrieval problem. In practice, however, much of the relevant information is already available in curated vendor systems, especially for quantitative analysis. We study whether, and under which conditions, Model Context Protocol (MCP) offers a more reliable alternative to standard retrieval-augmented generation (RAG) by allowing large language models (LLMs) to interact directly with data rather than relying on document ingestion and chunk retrieval. We test this by building a custom MCP server that exposes LSEG APIs as tools and evaluating it on the FinDER benchmark. The approach performs particularly well on the Financials subset, achieving up to 80.4% accuracy on multi-step numerical questions when relevant context is retrieved. The paper thus provides both a baseline for MCP-based financial question answering (QA) and evidence on where this approach breaks down, such as for questions requiring qualitative or document-specific context. Overall, direct access to curated data is a lightweight and effective alternative to document-centric RAG for quantitative financial QA, but not a substitute for all financial QA tasks.",
      links: [
        { text: "link to arXiv", url: "https://arxiv.org/abs/2603.20316" }
      ]
    }
  ];

  const renderPaper = (paper, index) => (
    <div key={index} className="working-paper-item">
      <h4>{paper.title}</h4>
      <p className="authors">
        {paper.authors}
        {paper.status && <span className="status"> - {paper.status}</span>}
      </p>
      <div className="abstract">
        <p><strong>Abstract:</strong> {paper.abstract}</p>
      </div>
      <div className="paper-links">
        {paper.links.map((link, linkIndex) => (
          <a
            key={linkIndex}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="paper-link"
          >
            {link.text} <FaExternalLinkAlt />
          </a>
        ))}
      </div>
    </div>
  );

  return (
    <section id="research" className="research section" ref={ref}>
      <div className="container">
        <div className="section-title">
          <h2>Research</h2>
          <p>My academic publications and ongoing research projects</p>
        </div>

        <div className="research-links">
          <a 
            href="https://scholar.google.de/citations?user=jgW3WbcAAAAJ&hl=en" 
            target="_blank" 
            rel="noopener noreferrer"
            className="research-link"
          >
            Google Scholar
          </a>
          <a 
            href="https://papers.ssrn.com/sol3/cf_dev/AbsByAuth.cfm?per_id=2483556" 
            target="_blank" 
            rel="noopener noreferrer"
            className="research-link"
          >
            SSRN
          </a>
          <a
            href="https://arxiv.org/search/?query=Mansouri%2C+Sasan&searchtype=author"
            target="_blank"
            rel="noopener noreferrer"
            className="research-link"
          >
            arXiv
          </a>
          <a
            href="/econlinguistics/"
            className="research-link"
          >
            Data &amp; Tools
          </a>
        </div>

        <div className="research-content">
          {/* Publications Section */}
          <div className="research-section">
            <button 
              className="section-header"
              onClick={() => toggleSection('publications')}
            >
              {expandedSections.publications ? <FaChevronDown /> : <FaChevronRight />}
              <h3>Publications</h3>
            </button>
            
            {expandedSections.publications && (
              <div className="section-content">
                {publications.map((pub, index) => (
                  <div key={index} className="publication-item">
                    <h4>{pub.title}</h4>
                    <p className="authors">{pub.authors} - {pub.journal}</p>
                    <div className="publication-links">
                      {pub.links.map((link, linkIndex) => (
                        <a 
                          key={linkIndex}
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="publication-link"
                        >
                          {link.text} <FaExternalLinkAlt />
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Working Papers Section */}
          <div className="research-section">
            <button 
              className="section-header"
              onClick={() => toggleSection('workingPapers')}
            >
              {expandedSections.workingPapers ? <FaChevronDown /> : <FaChevronRight />}
              <h3>Working Papers</h3>
            </button>
            
            {expandedSections.workingPapers && (
              <div className="section-content">
                {workingPapers.map(renderPaper)}
              </div>
            )}
          </div>

          {/* Computer Science Preprints Section */}
          <div className="research-section">
            <button
              className="section-header"
              onClick={() => toggleSection('csPreprints')}
            >
              {expandedSections.csPreprints ? <FaChevronDown /> : <FaChevronRight />}
              <h3>Computer Science Preprints</h3>
            </button>

            {expandedSections.csPreprints && (
              <div className="section-content">
                {csPreprints.map(renderPaper)}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Research; 